"use client";

import { useEffect, useState, useMemo } from "react";
import { ticketApi, type Ticket } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(iso: string | null, locale = "en-GB"): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(locale, {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function fmtDateTime(iso: string | null, locale = "en-GB"): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(locale, {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

// ─── Badges ───────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  open:     "bg-blue-100 text-blue-700",
  closed:   "bg-gray-100 text-gray-600",
  resolved: "bg-green-100 text-green-700",
  archived: "bg-gray-100 text-gray-500",
};

function StatusBadge({ value }: { value: string }) {
  const cls = STATUS_COLORS[value?.toLowerCase()] ?? "bg-yellow-100 text-yellow-700";
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${cls}`}>
      {value || "—"}
    </span>
  );
}

const PRIORITY_COLORS: Record<string, string> = {
  p1:        "bg-red-100 text-red-700",
  p2:        "bg-orange-100 text-orange-700",
  p3:        "bg-yellow-100 text-yellow-700",
  p4:        "bg-green-100 text-green-700",
  high:      "bg-red-100 text-red-700",
  normal:    "bg-blue-100 text-blue-700",
  low:       "bg-green-100 text-green-700",
  emergency: "bg-red-200 text-red-800",
};

function PriorityBadge({ value }: { value: string }) {
  const cls = PRIORITY_COLORS[value?.toLowerCase()] ?? "bg-gray-100 text-gray-600";
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold uppercase ${cls}`}>
      {value || "—"}
    </span>
  );
}

const APPROVAL_COLORS: Record<string, string> = {
  in_review: "bg-yellow-100 text-yellow-700",
  approved:  "bg-green-100 text-green-700",
  discarded: "bg-gray-100 text-gray-500",
};

function ApprovalBadge({ value }: { value: string }) {
  const cls = APPROVAL_COLORS[value] ?? "bg-gray-100 text-gray-600";
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${cls}`}>
      {value?.replace("_", " ") || "—"}
    </span>
  );
}

// ─── Detail drawer ────────────────────────────────────────────────────────────

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-2 py-1.5 border-b last:border-0" style={{ borderColor: "var(--card-border)" }}>
      <span className="min-w-[9rem] max-w-[12rem] shrink-0 text-xs font-medium" style={{ color: "var(--text-muted)" }}>{label}</span>
      <span className="text-xs flex-1 min-w-0" style={{ color: "var(--text-heading)" }}>{value ?? "—"}</span>
    </div>
  );
}

function TicketDrawer({ ticket, onClose }: { ticket: Ticket; onClose: () => void }) {
  const { t, lang, isAr } = useI18n();
  const locale = lang === "ar" ? "ar-SA" : "en-GB";
  return (
    <div className={`fixed inset-0 z-50 flex ${isAr ? "justify-start" : "justify-end"}`}>
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div
        className="relative w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl"
        style={{ borderInlineStart: "1px solid var(--card-border)" }}
      >
        <div
          className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 border-b"
          style={{ background: "var(--section-header-bg)", borderColor: "var(--card-border)" }}
        >
          <div>
            <p className="text-xs font-medium" style={{ color: "rgba(255,255,255,0.7)" }}>{t("Ticket","تذكرة")} {ticket.ticket_number || ticket.ticket_id}</p>
            <h2 className="text-sm font-bold mt-0.5" style={{ color: "#ffffff", fontFamily: "var(--font-brand), serif" }}>
              {ticket.subject}
            </h2>
            {ticket.external_url && (
              <a
                href={ticket.external_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-2 text-xs font-semibold underline"
                style={{ color: "rgba(255,255,255,0.9)" }}
              >
                {t("Open in Zammad", "فتح في Zammad")}
              </a>
            )}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10 transition-colors" style={{ color: "#ffffff" }}>
            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: "var(--text-muted)" }}>{t("Content", "المحتوى")}</p>
            <div
              className="text-sm leading-relaxed p-3 rounded-lg prose prose-sm max-w-none"
              style={{ background: "#f9fafb", color: "var(--text-heading)" }}
              dangerouslySetInnerHTML={{ __html: ticket.ticket_content || "—" }}
            />
          </div>

          {ticket.response && (
            <div className="rounded-lg p-3" style={{ background: "#f0f4fa" }}>
              <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: "var(--text-muted)" }}>{t("AI Response", "رد الذكاء الاصطناعي")}</p>
              <div
                className="text-sm leading-relaxed prose prose-sm max-w-none"
                style={{ color: "var(--text-heading)" }}
                dangerouslySetInnerHTML={{ __html: ticket.response }}
              />
            </div>
          )}

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: "var(--text-muted)" }}>{t("Details", "التفاصيل")}</p>
            <div className="rounded-lg border overflow-hidden" style={{ borderColor: "var(--card-border)" }}>
              <div className="px-3 py-1">
                <DetailRow label={t("DB ID","رقم قاعدة البيانات")}         value={ticket.id} />
                <DetailRow label={t("Ticket ID","رقم التذكرة")}             value={ticket.ticket_id} />
                <DetailRow label={t("Ticket Number","رقم التذكرة")}         value={ticket.ticket_number} />
                <DetailRow label={t("Status","الحالة")}                     value={<StatusBadge value={ticket.status} />} />
                <DetailRow label={t("Ticket Status","حالة التذكرة")}        value={ticket.ticket_status} />
                <DetailRow label={t("Priority","الأولوية")}                 value={<PriorityBadge value={ticket.priority} />} />
                <DetailRow label={t("Department","القسم")}                  value={ticket.department} />
                <DetailRow label={t("SLA","اتفاقية مستوى الخدمة")}          value={ticket.sla} />
                <DetailRow label={t("Source","المصدر")}                     value={ticket.source} />
                <DetailRow label={t("Type","النوع")}                        value={ticket.ticket_type} />
                <DetailRow label={t("Sender","المُرسِل")}                    value={ticket.sender} />
                <DetailRow label={t("Sender Email","بريد المُرسِل")}         value={ticket.sender_email} />
                <DetailRow label={t("Is Overdue","متأخرة؟")}                value={ticket.is_overdue ? t("Yes","نعم") : t("No","لا")} />
                <DetailRow label={t("Is Answered","مُجابة؟")}               value={ticket.is_answered ? t("Yes","نعم") : t("No","لا")} />
                <DetailRow label={t("Due Date","تاريخ الاستحقاق")}          value={fmtDateTime(ticket.due_date, locale)} />
                <DetailRow label={t("Closed At","أُغلقت في")}               value={fmtDateTime(ticket.closed, locale)} />
                <DetailRow label={t("OSTicket Created","تاريخ إنشاء التذكرة")} value={fmtDateTime(ticket.osticket_created, locale)} />
                <DetailRow label={t("OSTicket Updated","تاريخ تحديث التذكرة")} value={fmtDateTime(ticket.osticket_updated, locale)} />
                <DetailRow label={t("Generation Status","حالة الإنشاء")}    value={ticket.generation_status} />
                <DetailRow label={t("Job ID","رقم المهمة")}                  value={ticket.job_id} />
                <DetailRow label={t("AI Confidence","ثقة الذكاء الاصطناعي")} value={ticket.ai_confidence_score != null ? `${(ticket.ai_confidence_score * 100).toFixed(1)}%` : null} />
                <DetailRow label={t("Human Approval","موافقة بشرية")}        value={<ApprovalBadge value={ticket.human_approval_status} />} />
                <DetailRow label={t("Created At","تاريخ الإنشاء")}           value={fmtDateTime(ticket.created_at, locale)} />
                <DetailRow label={t("Updated At","تاريخ التحديث")}           value={fmtDateTime(ticket.updated_at, locale)} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function TicketsPage() {
  const { t, lang } = useI18n();
  const locale = lang === "ar" ? "ar-SA" : "en-GB";
  const [tickets, setTickets]           = useState<Ticket[]>([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState("");
  const [search, setSearch]             = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected]         = useState<Ticket | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  function fetchTickets() {
    setLoading(true);
    setError("");
    ticketApi
      .getTickets()
      .then(setTickets)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "Failed to load tickets."))
      .finally(() => setLoading(false));
  }

  useEffect(() => { fetchTickets(); }, []);

  function openTicket(ticket: Ticket) {
    setSelected(ticket);
    setDetailLoading(true);
    ticketApi
      .getTicket(ticket.id)
      .then(setSelected)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "Failed to load ticket details."))
      .finally(() => setDetailLoading(false));
  }

  const isZammadSource = tickets.some((t) => t.source === "Zammad");

  const statuses = useMemo(() => {
    const s = new Set(tickets.map((t) => t.status?.toLowerCase()).filter(Boolean));
    return ["all", ...Array.from(s).sort()];
  }, [tickets]);

  const visible = useMemo(() => {
    return tickets.filter((t) => {
      const matchStatus = statusFilter === "all" || t.status?.toLowerCase() === statusFilter;
      const q = search.toLowerCase();
      const matchSearch =
        !q ||
        t.subject?.toLowerCase().includes(q) ||
        t.sender?.toLowerCase().includes(q) ||
        t.sender_email?.toLowerCase().includes(q) ||
        t.ticket_id?.toLowerCase().includes(q) ||
        t.ticket_number?.toLowerCase().includes(q);
      return matchStatus && matchSearch;
    });
  }, [tickets, statusFilter, search]);

  const kpi = useMemo(() => ({
    open:     tickets.filter((t) => t.status?.toLowerCase() === "open").length,
    resolved: tickets.filter((t) => ["closed", "resolved"].includes(t.status?.toLowerCase())).length,
    overdue:  tickets.filter((t) => t.is_overdue).length,
    inReview: tickets.filter((t) => t.human_approval_status === "in_review").length,
  }), [tickets]);

  return (
    <div className="space-y-6" style={{ padding: "var(--page-padding)" }}>
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="page-title">{t("Tickets", "التذاكر")}</h1>
          <p className="page-subtitle">{t("Browse and manage support tickets", "تصفح وإدارة تذاكر الدعم")}</p>
        </div>
        <button
          type="button"
          onClick={fetchTickets}
          className="px-4 py-2 rounded-lg text-sm font-bold text-white"
          style={{ background: "var(--primary-btn)", fontFamily: "var(--font-brand), serif" }}
        >
          {t("Refresh", "تحديث")}
        </button>
      </div>

      {/* KPI strip */}
      <div className={`grid grid-cols-2 ${isZammadSource ? "sm:grid-cols-3" : "sm:grid-cols-4"} gap-4`}>
        {[
          { label: t("Open", "مفتوحة"),                         value: kpi.open },
          { label: t("Resolved / Closed", "محلولة / مغلقة"),   value: kpi.resolved },
          { label: t("Overdue", "متأخرة"),                      value: kpi.overdue },
          ...(!isZammadSource ? [{ label: t("Awaiting Approval", "بانتظار الموافقة"), value: kpi.inReview }] : []),
        ].map(({ label, value }) => (
          <div key={label} className="rounded-lg border bg-white p-4" style={{ borderColor: "var(--card-border)", boxShadow: "var(--card-shadow)" }}>
            <p className="text-xs font-medium mb-2" style={{ color: "var(--text-muted)" }}>{label}</p>
            <span className="text-2xl font-bold" style={{ color: "var(--text-value)", fontFamily: "var(--font-brand), serif" }}>
              {loading ? "…" : value}
            </span>
          </div>
        ))}
      </div>

      {/* Ticket table */}
      <div className="rounded-lg overflow-hidden border" style={{ borderColor: "var(--card-border)", boxShadow: "var(--card-shadow)" }}>
        {/* Panel header */}
        <div className="px-4 py-3 flex items-center justify-between" style={{ background: "var(--section-header-bg)" }}>
          <h3 className="text-sm font-semibold" style={{ color: "var(--section-header-text)", fontFamily: "var(--font-brand), serif" }}>
            {visible.length !== tickets.length
              ? `${t("Tickets", "التذاكر")} (${visible.length} ${t("of", "من")} ${tickets.length})`
              : `${t("Tickets", "التذاكر")} (${tickets.length})`}
          </h3>
          <span className="text-xs" style={{ color: "var(--text-muted)" }}>{t("Click a row for full details", "انقر على صف للتفاصيل الكاملة")}</span>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 px-4 py-3 border-b bg-white" style={{ borderColor: "var(--card-border)" }}>
          <div className="relative flex-1 min-w-[200px]">
            <svg
              className="absolute start-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
              width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"
              style={{ color: "var(--text-muted)" }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 111 11a6 6 0 0116 0z" />
            </svg>
            <input
              type="text"
              placeholder={t("Search by subject, sender, or ticket ID…", "ابحث بالموضوع أو المُرسِل أو رقم التذكرة…")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full ps-8 pe-3 py-1.5 text-sm rounded-lg border focus:outline-none"
              style={{ borderColor: "var(--card-border)" }}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-lg border focus:outline-none bg-white"
            style={{ borderColor: "var(--card-border)", color: "var(--text-heading)" }}
          >
            {statuses.map((s) => (
              <option key={s} value={s}>
                {s === "all" ? t("All statuses", "جميع الحالات") : s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Table */}
        <div className="overflow-x-auto bg-white">
          {error && <p className="px-4 py-3 text-sm text-red-600">{error}</p>}
          {loading ? (
            <p className="px-4 py-10 text-center text-sm" style={{ color: "var(--text-muted)" }}>{t("Loading tickets…", "جارٍ تحميل التذاكر…")}</p>
          ) : visible.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm" style={{ color: "var(--text-muted)" }}>
              {tickets.length === 0 ? t("No tickets found.", "لا توجد تذاكر.") : t("No tickets match the current filters.", "لا توجد تذاكر تطابق المرشحات الحالية.")}
            </p>
          ) : (
            <table className="min-w-full">
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  {[
                    t("Ticket ID", "رقم التذكرة"),
                    t("Subject", "الموضوع"),
                    t("Sender", "المُرسِل"),
                    t("Status", "الحالة"),
                    t("Priority", "الأولوية"),
                    ...(isZammadSource ? [] : [t("Type", "النوع"), t("AI Approval", "موافقة الذكاء الاصطناعي")]),
                    t("Due Date", "تاريخ الاستحقاق"),
                    t("Created", "تاريخ الإنشاء"),
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-start text-xs font-medium uppercase tracking-wider whitespace-nowrap"
                      style={{ color: "var(--text-muted)" }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((t, i) => (
                  <tr
                    key={t.id}
                    onClick={() => openTicket(t)}
                    className="cursor-pointer hover:bg-[#f9fafb] transition-colors"
                    style={{ borderTop: i > 0 ? "1px solid var(--card-border)" : undefined }}
                  >
                    <td className="px-4 py-3 text-xs font-mono whitespace-nowrap" style={{ color: "var(--text-muted)" }}>
                      {t.ticket_number || t.ticket_id}
                    </td>
                    <td className="px-4 py-3 text-sm max-w-[240px]" style={{ color: "var(--text-heading)" }}>
                      <span className="block truncate">{t.subject}</span>
                    </td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: "var(--text-heading)" }}>
                      <div>{t.sender}</div>
                      <div style={{ color: "var(--text-muted)" }}>{t.sender_email}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <StatusBadge value={t.status} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <PriorityBadge value={t.priority} />
                    </td>
                    {!isZammadSource && (
                      <>
                        <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: "var(--text-muted)" }}>
                          {t.ticket_type || "—"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <ApprovalBadge value={t.human_approval_status} />
                        </td>
                      </>
                    )}
                    <td
                      className="px-4 py-3 text-xs whitespace-nowrap"
                      style={{ color: t.is_overdue ? "#dc2626" : "var(--text-muted)" }}
                    >
                      {fmt(t.due_date, locale)}
                      {t.is_overdue && <span className="ml-1 text-red-500">!</span>}
                    </td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: "var(--text-muted)" }}>
                      {fmt(t.created_at, locale)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {selected && (
        <>
          {detailLoading && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-none">
              <div className="px-4 py-2 rounded-lg bg-black/70 text-white text-sm">{t("Loading details…", "جارٍ تحميل التفاصيل…")}</div>
            </div>
          )}
          <TicketDrawer ticket={selected} onClose={() => setSelected(null)} />
        </>
      )}
    </div>
  );
}
